import { ClientFactory } from "@/main/common/ClientFactory";
import type {
    FetchedHtmlDocument,
    FrontendReleaseProbeResult,
} from "@/main/navigation/NavigationTypes";

/**
 * U44 (FI-3676, r156, ui-fundamentals §7c): anticipate the tap. A press on a soft link (`NavigationPrefetch`, on
 * `pointerdown` only) may start the destination's critical-path request here, and the navigation that follows takes
 * it over instead of issuing its own. Each prefetched request is handed over ONCE and only within
 * {@link PREFETCH_HANDOVER_TTL_MS}: after that the navigation fetches fresh, so a press long ago can never serve a
 * stale page. A prefetch that FAILED is never handed over as a failure; the navigation retries with its own request.
 *
 * <p>The navigation's abort signal still governs what the navigation sees: aborting it rejects with the same
 * `AbortError` the catch ladder in `runEnhancedNavigation` already distinguishes, while the shared request finishes
 * in the background (a GET, so finishing it changes nothing).
 */
export const PREFETCH_HANDOVER_TTL_MS = 10_000;

type PrefetchedRequest<T> = { promise: Promise<T>; issuedAt: number };

const prefetchedDocuments = new Map<string, PrefetchedRequest<FetchedHtmlDocument>>();
const prefetchedReleaseProbes = new Map<string, PrefetchedRequest<FrontendReleaseProbeResult>>();

function isLive<T>(entry: PrefetchedRequest<T> | undefined): entry is PrefetchedRequest<T> {
    return entry !== undefined && Date.now() - entry.issuedAt <= PREFETCH_HANDOVER_TTL_MS;
}

function takePrefetched<T>(store: Map<string, PrefetchedRequest<T>>, href: string): Promise<T> | null {
    const entry = store.get(href);
    store.delete(href);
    return isLive(entry) ? entry.promise : null;
}

function createAbortError(): Error {
    const abortError = new Error("The operation was aborted.");
    abortError.name = "AbortError";
    return abortError;
}

/** The shared request, seen through this navigation's own signal. */
function underSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
    if (!signal) {
        return promise;
    }
    if (signal.aborted) {
        return Promise.reject(createAbortError());
    }
    return new Promise<T>((resolve, reject) => {
        const onAbort = () => reject(createAbortError());
        signal.addEventListener("abort", onAbort, { once: true });
        promise.then(
            (value) => {
                signal.removeEventListener("abort", onAbort);
                resolve(value);
            },
            (error: unknown) => {
                signal.removeEventListener("abort", onAbort);
                reject(error);
            },
        );
    });
}

function remember<T>(store: Map<string, PrefetchedRequest<T>>, href: string, start: () => Promise<T>): boolean {
    if (isLive(store.get(href))) {
        return false;
    }
    const promise = start();
    // A prefetch nobody consumes must not surface as an unhandled rejection.
    promise.catch(() => {});
    store.set(href, { promise, issuedAt: Date.now() });
    return true;
}

export async function fetchHtmlDocument(url: URL, signal?: AbortSignal): Promise<FetchedHtmlDocument> {
    const prefetched = takePrefetched(prefetchedDocuments, url.href);
    if (prefetched) {
        try {
            return await underSignal(prefetched, signal);
        } catch (error) {
            if (signal?.aborted) {
                throw error;
            }
            // The press-time request failed: the navigation makes its own, exactly as it would have without U44.
        }
    }
    return ClientFactory.getNavigationDocumentApi().fetchHtmlDocument(url, signal);
}

export async function probeFrontendRelease(url: URL, signal?: AbortSignal): Promise<FrontendReleaseProbeResult> {
    const prefetched = takePrefetched(prefetchedReleaseProbes, url.href);
    if (prefetched) {
        try {
            return await underSignal(prefetched, signal);
        } catch (error) {
            if (signal?.aborted) {
                throw error;
            }
        }
    }
    return ClientFactory.getNavigationDocumentApi().probeFrontendRelease(url, signal);
}

/** U44: start a document-swap destination's HTML fetch on press. False when a live one is already in flight. */
export function prefetchHtmlDocument(url: URL): boolean {
    return remember(prefetchedDocuments, url.href, () => ClientFactory.getNavigationDocumentApi().fetchHtmlDocument(url));
}

/** U44: start a client-route destination's release probe on press. False when a live one is already in flight. */
export function prefetchFrontendReleaseProbe(url: URL): boolean {
    return remember(prefetchedReleaseProbes, url.href, () => ClientFactory.getNavigationDocumentApi().probeFrontendRelease(url));
}

export function resetDocumentPrefetchForTests(): void {
    prefetchedDocuments.clear();
    prefetchedReleaseProbes.clear();
}
